import * as React from 'react';
import autobind from 'autobind-decorator';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Modal, Alert, Col, Row, Collapse, List } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { BooleanView } from 'src/core/ui/boolean.view';
import { DataTableView, Column, ToolbarVisibility } from 'src/core/ui/data-table.view';
import { TransferCard } from 'src/app/bookings/search/results/search-results-transfer.view';
import { ProductServiceTypeEditorController } from 'src/app/products/product-service-types/parts/product-service-type-editor.controller';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { ObjectUtils } from 'src/core/utils/object';
import { FormatService } from 'src/core/services/format.service';

interface AutoConectTransferViewProps {
	showModal: boolean
	setBusy(isBusy: boolean);
	apiNames: Api[]
	onAceptSelectedTransfers: (transfers: ResultSelection[], transferParam) => Promise<void>;
	onAutoConect: (parameters: SearchAutoConnetTransfer[]) => Promise<ApiResultAutoConnectTransfer>;
	closeAutocconetDialog: (ev?: React.MouseEvent<HTMLButtonElement>) => void;
	headerMsg: string;
	autoConnectTransferItems: AutoConnectTranferItem[];
	quote: QuoteItem;
	formatService: FormatService
	makeParameter: (apiNames: Api[], parameters: AutoConnectTranferItem[]) => SearchAutoConnetTransfer[]
}

interface AutoConectTransferState {
	selections?: { [keyGroup: string]: { [unitIndex: string]: ResultSelection } };
	selectedEntries: AutoConnectTranferItem[]
	categories: { [entryId: string]: number; }
	services: { [entryId: string]: number; }
}

@observer
export class AutoConectTransferView extends React.Component<AutoConectTransferViewProps, AutoConectTransferState> {
	public state: AutoConectTransferState = {
		selections: {},
		selectedEntries: this.props.autoConnectTransferItems,
		categories: {},
		services: {}
	};

	@observable
	private showResult: boolean = this.props.showModal;

	@observable
	private showModal: boolean = false;

	private results: AutoconnectTransferSearchResult[];

	@observable
	private dataSet: DataLocalService<ProductSearchResult>;

	@observable
	private messages: DataLocalService<Message>;

	@observable
	private searchProducts = false;

	private parameters: SearchAutoConnetTransfer[] = [];

	@observable
	private categoryId: number;

	@observable
	private serviceId: number;

	private columns: Column[] = [
		{
			key: 'fromProductName',
			dataIndex: 'fromProductName',
			title: 'From',
			className: 'product-name',
		},
		{
			key: 'toProductName',
			dataIndex: 'toProductName',
			title: 'To',
			className: 'product-name',
		},
		{
			key: 'date',
			dataIndex: 'date',
			title: 'Date',
			render: (text: any) => {
				return this.props.formatService.toString('small-date-time', text, "utc");
			},
		},
		{
			key: 'category',
			dataIndex: 'category',
			title: 'Category',
			width: 200,
			render: (text: any, record: AutoConnectTranferItem) => {
				return (
					<ProductCategoryEditorController
						label={null}
						mode="edit"
						onChanged={(value) => {
							record.productCategoryId = value != null ? value.id : null;
							const categories = this.state.categories;
							categories[record.id] = value != null ? value.id : null;
							this.setState({ categories: categories });

						}}
						value={record.productCategoryId}
						productType={3}
						setBusy={this.props.setBusy}
					/>
				);
			},
		},
		{
			key: 'service',
			dataIndex: 'service',
			title: 'Service',
			width: 200,
			render: (text: any, record: AutoConnectTranferItem) => {
				return (
					<ProductServiceTypeEditorController
						label={null}
						categoryId={record.productCategoryId}
						mode={record.productCategoryId ? 'edit' : 'display'}
						onChanged={(value) => {
							record.productServiceTypeId = value != null ? value.id : null;
							const services = this.state.services;
							services[record.id] = value != null ? value.id : null;
							this.setState({ services: services });
						}}
						value={record.productServiceTypeId}
						setBusy={this.props.setBusy}
					/>
				);
			},
		},
		{
			key: 'paxs',
			dataIndex: 'paxs',
			title: 'Paxs',
			render: (text: any, record: AutoConnectTranferItem) => {
				return this.renderPaxs(record.paxs)
			},
		},
	];

	private messageColumn: Column[] = [
		{
			key: 'severity',
			title: 'Severity',
			dataIndex: 'severity',
			render: (text: string, record: Message) => {
				switch (record.severity) {
					case 0:
						return 'Info';
					case 1:
						return 'Warning';
					case 2:
						return 'Error';
				}
				return null;
			}
		},
		{
			key: 'text',
			title: 'Message',
			dataIndex: 'text',
		}
	];

	public componentWillReceiveProps(nextProps: Readonly<AutoConectTransferViewProps>, nextContext: any) {
		if (!(ObjectUtils.equals(this.props.quote, nextProps.quote) && ObjectUtils.equals(this.props.autoConnectTransferItems, nextProps.autoConnectTransferItems))) {
			this.setState({
				selectedEntries: nextProps.autoConnectTransferItems
			});
		}
		if (this.props.showModal != nextProps.showModal) {
			this.showModal = nextProps.showModal;
		}
	}

	public render() {
		const { selectedEntries } = this.state;
		const buttons = [
			<Button key="cancel" onClick={this.closeDialog}>Cancel</Button>
		];
		if (this.showResult == true) {
			buttons.unshift(<Button key="confirm"
				type="primary"
				onClick={() => this.onAcceptClick()}
				disabled={!this.showResult}>OK</Button>);
		} else {
			buttons.unshift(
				<Button
					key="search"
					icon={this.searchProducts ? 'loading' : undefined}
					type="primary"
					onClick={() => this.onSearchAutoConectTransferClick()}
					disabled={selectedEntries.length == 0}
				>
					Search
				</Button>
			);
		}

		return (
			<Modal
				style={{ top: 20 }}
				width="80%"
				visible={this.showModal}
				onCancel={this.closeDialog}
				title="Auto Conect Transfers"
				maskClosable={false}
				footer={buttons}
			>
				<Row>
					{
						this.showResult == false && this.props.apiNames && this.props.autoConnectTransferItems.length >= 1 && this.renderSearch()
					}
				</Row>
				{
					this.showResult == false && this.props.apiNames && this.props.autoConnectTransferItems.length == 0 && <Alert
						message="Autoconnect"
						description="There are no products to autoconnect."
						type="warning"
					/>
				}
				<Row>
					<Col xs={24}>
						{
							this.showResult == true && this.results != null &&
							this.renderResult(this.results)
						}
					</Col>
				</Row>
			</Modal>
		);
	}

	private renderPaxs(paxs: string[]) {
		const paxView = [];
		for (const pax of paxs) {
			paxView.push(<b>{pax}</b>)
			paxView.push(<br></br>)
		}
		return <React.Fragment>{paxView}</React.Fragment>
	}

	@autobind
	private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		this.showResult = false;
		this.searchProducts = false;
		this.setState({
			selectedEntries: [...this.props.autoConnectTransferItems],
			categories: {},
			services: {}
		});
		this.props.closeAutocconetDialog(ev);
	}

	@autobind
	private async onSearchAutoConectTransferClick() {
		this.searchProducts = true;
		this.props.setBusy(true);
		const { apiNames } = this.props
		const { selectedEntries } = this.state;
		if (selectedEntries.length && this.categoryId) {
			selectedEntries.forEach(p => {
				p.productCategoryId = this.categoryId;
				p.productServiceTypeId = this.serviceId;
			});
		}
		this.parameters = this.props.makeParameter(apiNames, selectedEntries);

		const res = await this.props.onAutoConect(this.parameters);
		if (res) {
			this.results = res.items;
			this.messages = new DataLocalService(res.messages);
		}
		this.showResult = true;
		this.props.setBusy(false);
	}

	@autobind
	private async onAcceptClick(ev?: React.MouseEvent<HTMLButtonElement>) {
		this.props.setBusy(true);
		const { selections } = this.state;
		const selectedProducts = [];
		for (const prop in selections) {
			if (selections[prop]) {
				const properties = Object.getOwnPropertyNames(selections[prop]);
				for (const prop1 of properties) {
					selectedProducts.push(selections[prop][prop1]);
				}
			}
		}
		const res = await this.props.onAceptSelectedTransfers(selectedProducts, this.parameters[0].transferParam);
		this.closeDialog();
		this.props.setBusy(false);
	}

	private handleOnChangeSelectedRows = (selectedRowKeys: string[]): void => {
		const selectedEntries = this.props.autoConnectTransferItems.filter(e => selectedRowKeys.some(k => k == e.id));
		this.setState({
			selectedEntries: selectedEntries
		});
	};

	private renderSearch() {
		const apis = [];
		for (const api of this.props.apiNames) {
			if (api.isActiveForApi) {
				apis.push(
					<div key={api.name} style={{ display: "inline-block" }} className="mr-1">
						<BooleanView
							mode="edit"
							label={api.name}
							value={api.isActive}
							onChanged={newVal => api.isActive = newVal}
						/>
					</div>
				);
			}
		}
		const { autoConnectTransferItems } = this.props;
		const { selectedEntries } = this.state;
		const view = (
			<React.Fragment>
				<Row gutter={16}>
					<Col xs={24} lg={12}>
						<ProductCategoryEditorController
							mode="edit"
							onChanged={(value) => { this.categoryId = value != null ? value.id : null; }}
							value={this.categoryId}
							productType={3}
							setBusy={this.props.setBusy}
						/>
					</Col>
					<Col xs={24} lg={12}>
						<ProductServiceTypeEditorController
							categoryId={this.categoryId}
							mode={this.categoryId ? 'edit' : 'display'}
							onChanged={(value) => { this.serviceId = value != null ? value.id : null; }}
							value={this.serviceId}
							setBusy={this.props.setBusy}
						/>
					</Col>
				</Row>
				<Row gutter={16}>
					<DataTableView
						columns={this.columns}
						mode="display"
						setBusy={this.props.setBusy}
						model={new DataLocalService(autoConnectTransferItems) as DataSet<AutoConnectTranferItem>}
						rowKey="id"
						selection={{
							type: 'checkbox',
							selectedRowKeys: selectedEntries.map(x => x.id),
							onChange: this.handleOnChangeSelectedRows,
						}}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hidePagination | ToolbarVisibility.hideModifyButtons}
					/>
				</Row>
				<Row gutter={16}>
					{apis}
				</Row>
			</React.Fragment>
		);
		return view;
	}

	private onChangeSelection = (selectedRowKeys: string[] | number[], selectedRows: ResultOption[]) => {
		const { selections } = this.state;
		for (const selected of selectedRows) {
			const group = this.results.find(r => r.transfers.some(t => t.id == selected.id));
			const selection = group ? selections[`${group.fromEntryId}-${group.toEntryId}`] : {};
			if (selection[selected.unitIndex] && selection[selected.unitIndex].selected.id === selected.id) {
				continue;
			}
			selection[selected.unitIndex] = {
				selected: selected,
				supDisc: selected.result.subProducts
					.filter((e: SupplementSearchResult) => e.isRequired)
					.map((e: SupplementSearchResult) => ({
						id: e.id,
						name: e.product.name,
						unitIndex: selected.unitIndex,
						cost: e.costPrice[selected.unitIndex],
						sell: e.sellPrice[selected.unitIndex],
						isRequired: e.isRequired,
						result: e
					})),
			};
			selections[`${group.fromEntryId}-${group.toEntryId}`] = selection;
		}
		this.setState({
			selections: selections,
		});
	}

	private onChangeOptionalSelection = (selectedRowKeys: string[] | number[], selectedRows: ResultOption[]) => {
		const selections = { ...this.state.selections };
		for (const selected of selectedRows) {
			const group = this.results.find(r => r.transfers.some(t => t.id == selected.id));
			const selection = group ? selections[`${group.fromEntryId}-${group.toEntryId}`] : {};
			const result = selection[selected.unitIndex];
			if (result.supDisc == null)
				result.supDisc = [];
			if (result == null || selected.parent.id !== result.selected.id) {
				continue;
			}
			result.supDisc.push(selected);
		}
		this.setState({
			selections: selections,
		});
	}

	private renderTransferItem = (item: TransferSearchResult): JSX.Element => {
		const { setBusy } = this.props;
		const { selections } = this.state;
		const group = this.results.find(r => r.transfers.some(t => t.id == item.id));
		const selection = group && selections[`${group.fromEntryId}-${group.toEntryId}`] ? selections[`${group.fromEntryId}-${group.toEntryId}`] : {};
		const commonProps = {
			setBusy,
			selection,
			onChangeSelection: this.onChangeSelection,
			onChangeOptionalSelection: this.onChangeOptionalSelection
		};
		return (
			<List.Item key={item.product.internalId}>
				<TransferCard
					items={[item]}
					{...commonProps}
				/>
			</List.Item>
		);
	}

	private renderItem = (item: AutoconnectTransferSearchResult): JSX.Element => {
		const { selections } = this.state;
		if (selections[`${item.fromEntryId}-${item.toEntryId}`] == null) {
			selections[`${item.fromEntryId}-${item.toEntryId}`] = {};
		}
		return (
			<List.Item key={`${item.fromEntryId}-${item.toEntryId}`}>
				<List
					header={<strong>{item.title}</strong>}
					itemLayout="vertical"
					dataSource={item.transfers}
					renderItem={this.renderTransferItem}
				/>
			</List.Item>
		);
	}

	private renderResult(items: AutoconnectTransferSearchResult[]) {
		const headerMsg = this.messages && this.messages.count > 0
			? (
				<Collapse.Panel key="1" header={`Messages (${this.messages.count})`}>
					<DataTableView
						mode="display"
						setBusy={this.props.setBusy}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideFooter | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
						columns={this.messageColumn}
						model={this.messages as DataSet<Message>}
					/>
				</Collapse.Panel>
			)
			: undefined;
		const view = (
			<List
				itemLayout="vertical"
				dataSource={items}
				renderItem={this.renderItem}
			/>);
		return (
			<React.Fragment>
				<Collapse bordered={false} defaultActiveKey={['2']} >
					{headerMsg}
					<Collapse.Panel key="2" header={`Results (${items.length})`}>{view}</Collapse.Panel>
				</Collapse>
			</React.Fragment>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/auto-connect-transfer/auto-connect-tranfer.view.tsx